/**
 * Sesión del portal del paciente, separada de la del personal.
 *
 * Son dos sesiones que conviven de verdad: en el mostrador, la recepcionista
 * tiene la suya abierta mientras le enseña el portal a un paciente. Con un
 * único proveedor, entrar en uno echaba al otro.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  type MePortal,
  getMePortal,
  loginPortal,
} from "../api/portal";
import {
  clearPortalTokens,
  getPortalTokens,
  setPortalTokens,
} from "./tokens";

type Estado = "cargando" | "fuera" | "dentro";

interface PortalAuth {
  estado: Estado;
  me: MePortal | null;
  entrar: (slug: string, identificador: string, password: string) => Promise<MePortal>;
  salir: () => void;
  recargar: () => Promise<void>;
}

const Ctx = createContext<PortalAuth | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>("cargando");
  const [me, setMe] = useState<MePortal | null>(null);

  useEffect(() => {
    if (!getPortalTokens()) {
      setEstado("fuera");
      return;
    }
    getMePortal()
      .then((perfil) => {
        setMe(perfil);
        setEstado("dentro");
      })
      .catch(() => {
        clearPortalTokens();
        setMe(null);
        setEstado("fuera");
      });
  }, []);

  const entrar = useCallback(
    async (slug: string, identificador: string, password: string) => {
      const tokens = await loginPortal(slug, identificador, password);
      setPortalTokens(tokens);
      const perfil = await getMePortal();
      setMe(perfil);
      setEstado("dentro");
      return perfil;
    },
    [],
  );

  const salir = useCallback(() => {
    clearPortalTokens();
    setMe(null);
    setEstado("fuera");
  }, []);

  const recargar = useCallback(async () => {
    setMe(await getMePortal());
  }, []);

  return (
    <Ctx.Provider value={{ estado, me, entrar, salir, recargar }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePortalAuth(): PortalAuth {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePortalAuth debe usarse dentro de <PortalAuthProvider>");
  return ctx;
}
